import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import MemberCompetencyTable from '../../../components/tables/MemberCompetencyTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useMemberCompetencyApi } from '../../../hooks/useMemberCompetencyApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import {
  CompetencyTypeDTO,
  MemberCompetencyDTO,
} from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';
import { useCompetencyTypes } from '../../../hooks/useCompetencyTypeApi';
import { getRoles } from '../../../utils/tokenUtils';
import MemberCompetencyDataCard from '../../../components/dataCards/MemberCompetencyDataCard';
import { useViewMode } from '../../../utils/viewModeUtils/useViewMode';
import { ViewToggle } from '../../../components/common/ViewToggle';
import CompetencyOverview from '../../../components/dashboards/CompetencyOverview';
import { ViewModeConstants } from '../../../constants/ViewModeConstants';
import { getAdjustedPageAfterMutation } from '../../../utils/paginationUtils';
import ButtonLegendCta from '../../../components/common/ButtonLegendCta';
import { getButtonLegendItems } from '../../../utils/buttonLegendDisplayUtils';

const legendItems = getButtonLegendItems('memberCompetency');

const MemberCompetencyList: React.FC = () => {
  const { search, remove, isLoading, error } = useMemberCompetencyApi();
  const [items, setItems] = useState<MemberCompetencyDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  
  // Use TanStack Query hook for competency types (automatically cached)
  const { data: competencyTypesData = [], isLoading: isLoadingCompetencyTypes } = useCompetencyTypes();
  const competencyTypes = competencyTypesData as CompetencyTypeDTO[];
  
  const roles = getRoles();

  const { view, toggle } = useViewMode(
    'member-competencies',
    roles?.includes('User') ? ViewModeConstants.DATA_CARDS : ViewModeConstants.TABLE,
  );

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Competencies', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.data?.results || []);
      setTotalRecords(response?.data?.totalCount || 0);
      return response?.data?.totalCount || 0;
    } catch (error) {
      console.error('Error fetching Member Competencies:', error);
      return 0;
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  /**
   * Refreshes the list after a mutation (delete, update, etc.),
   * automatically adjusting to a valid page if the current page is now out of range.
   */
  const refreshAfterMutation = async () => {
    // Fetch with current page first to get the new total count
    const newTotalCount = await fetchItems(currentPage, pageSize, searchFilters);
    
    // Calculate what page we should be on based on new total
    const adjustedPage = getAdjustedPageAfterMutation(currentPage, pageSize, newTotalCount);
    
    // If we need to move to a different page, update and refetch
    if (adjustedPage !== currentPage) {
      setCurrentPage(adjustedPage);
      await fetchItems(adjustedPage, pageSize, searchFilters);
    }
  };

  const handleDelete = async (dto: MemberCompetencyDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await remove(dto.id || '');
      await refreshAfterMutation();
    } catch (error) {
      console.error('Error deleting MemberCompetency:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/membercompetencies"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin', 'User']}
      requiredPermission="Manage.MemberCompetency.View"
    >
      <Card
        title="Manage Member Competencies"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <div className="flex items-center gap-2 sm:gap-3">
            {roles?.includes('Admin') && (
              <Button
                type="button"
                variant="secondary"
                onClick={() => setShowAdvancedSearch(!showAdvancedSearch)}
              >
                {showAdvancedSearch ? "Hide Advanced Search" : "Advanced Search"}
              </Button>
            )}

            {roles?.includes('User') && (
              <>
                <ButtonLegendCta items={legendItems} />
                <AddNewButton
                  label='Add a Competency'
                  onClick={() => navigate('/admin/membercompetencies/create')}
                />
              </>
            )}
          </div>
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          {showAdvancedSearch && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <FormField
                id="competencyTypeId"
                label="Search by Competency Type"
                value={searchFilters.competencyTypeId || ''}
                type="dropdown"
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    competencyTypeId: e.target.value,
                  })
                }
                placeholder="Search by Competency Type Id"
                options={competencyTypes.map((type) => ({
                  value: type.id,
                  label: type.title || '',
                }))}
                isLoading={isLoading}
              />
              <FormField
                id="certificateNumber"
                label="Search by Certificate Number"
                value={searchFilters.certificateNumber || ''}
                onChange={(e) =>
                  setSearchFilters({
                    ...searchFilters,
                    certificateNumber: e.target.value,
                  })
                }
                placeholder="Search by Certificate Number"
              />
              <FormField
                id="createdFrom"
                label="Created From"
                type="date"
                value={searchFilters.createdFrom || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdFrom: e.target.value })
                }
              />
              <FormField
                id="createdTo"
                label="Created To"
                type="date"
                value={searchFilters.createdTo || ''}
                onChange={(e) =>
                  setSearchFilters({ ...searchFilters, createdTo: e.target.value })
                }
              />
            </div>
          )}

          <div className="flex justify-between items-center">
            <div className="space-x-4 mb-4">
              {showAdvancedSearch && (
                <>
                  <Button variant="secondary" onClick={handleClearSearch}>
                    Clear Search
                  </Button>
                  <Button type="submit">Search</Button>
                </>
              )}
            </div>

            <div className="hidden md:block">
              {roles?.includes('User') && (
                <ViewToggle view={view} toggle={toggle} />
              )}
            </div>
          </div>
        </form>

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
          {/* Left: Overview */}
          {roles?.includes('User') && (
            <aside className="xl:col-span-3">
              <div className="xl:sticky xl:top-4">
                <CompetencyOverview items={items} />
              </div>
            </aside>
          )}

          {/* Right: Filters + List */}
          <section className={roles?.includes('User') ? "xl:col-span-9" : "xl:col-span-12"}>
            {/* keep your search form here */}
            {(roles?.includes('Admin') || view === ViewModeConstants.TABLE) ? (
              <MemberCompetencyTable
                data={items}
                handleEdit={(dto) => navigate(`/admin/membercompetencies/${dto.id}`)}
                handleDelete={handleDelete}
                currentPage={currentPage}
                totalPages={Math.ceil(totalRecords / pageSize)}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isLoading={isLoading}
              />
            ) : (
              <MemberCompetencyDataCard
                data={items}
                handleEdit={(dto) => navigate(`/admin/membercompetencies/${dto.id}`)}
                handleRewal={() => navigate(`/admin/memberendorsements/`)}
                handleDelete={handleDelete}
                currentPage={currentPage}
                totalPages={Math.ceil(totalRecords / pageSize)}
                setCurrentPage={setCurrentPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                isLoading={isLoading}
              />
            )}
          </section>
        </div>




      </Card>
    </AdminLayout>
  );
};

export default MemberCompetencyList;
